import { Link } from "react-router-dom";
import { Settings, ArrowRight } from "lucide-react";
import BeratungDetailLayout, { BeratungSection } from "@/components/BeratungDetailLayout";

const BeratungProzessberatung = () => (
  <BeratungDetailLayout
    icon={Settings}
    title="Prozessberatung"
    intro="Wir analysieren Ihre internen Prozessabläufe und Kommunikation, schaffen Räume für offene Auseinandersetzung und denken die entscheidenden Regeln gemeinsam neu."
    seoTitle="Prozessberatung – Leadership Focus"
    seoDescription="Prozessberatung: Engpässe sichtbar machen, Entscheidpunkte und Schnittstellen klären, Abstimmungsschleifen und Meetinglast reduzieren."
    canonical="https://leadership-focus.de/portfolio/beratung/prozessberatung"
  >
    <BeratungSection heading="Wenn …">
      <p>
        Prozesse existieren – und trotzdem entstehen Schleifen: zu viele Abstimmungen, unklare
        Übergaben, Entscheidungen bleiben hängen. Meetings ersetzen Klarheit, operative Teams
        verlieren Zeit, und Führung spürt: „Wir sind beschäftigt, aber nicht schnell.“ HR/OE sieht oft
        zusätzlich: Kommunikation ist der Engpass, nicht die Prozesslandkarte.
      </p>
    </BeratungSection>

    <BeratungSection heading="Typische Anlässe">
      <ul className="list-disc pl-5 space-y-1">
        <li>Hohe Meetinglast, lange Durchlaufzeiten</li>
        <li>Schnittstellenkonflikte zwischen Bereichen/Standorten</li>
        <li>Unklare Entscheidpunkte („Wer darf das eigentlich?“)</li>
        <li>Qualität leidet durch Nacharbeit und Eskalationen</li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Dann …">
      <p>
        … analysieren wir gemeinsam, wo Arbeit stecken bleibt: an Übergaben, Rollen, Entscheidpunkten
        und Informationswegen. Wir schaffen gezielte Räume, in denen die Beteiligten offen auf das
        System schauen können – und gestalten die wenigen Prozess- und Kommunikationsregeln neu, die
        den größten Hebel haben.
      </p>
    </BeratungSection>

    <BeratungSection heading="Vorgehen (kompakt)">
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong className="font-semibold text-foreground">Engpass sichtbar machen:</strong> Wo
          verzögert sich Arbeit – und wodurch?
        </li>
        <li>
          <strong className="font-semibold text-foreground">
            Entscheidpunkte &amp; Schnittstellen klären:
          </strong>{" "}
          Wer entscheidet was, wann, mit welchen Informationen?
        </li>
        <li>
          <strong className="font-semibold text-foreground">Routinen stabilisieren:</strong>{" "}
          Meeting-/Info-Takt, klare Übergaben, Feedbackschleifen.
        </li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Ergebnis …">
      <ul className="list-disc pl-5 space-y-1">
        <li>Weniger Schleifen, schnellere Durchlaufzeiten, weniger Nacharbeit</li>
        <li>Klare Schnittstellen und Entscheiderlogiken</li>
        <li>Kommunikationsroutinen, die im Alltag halten</li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Messsignale">
      <ul className="list-disc pl-5 space-y-1">
        <li>Durchlaufzeit/Lead Time an 1–2 kritischen Prozessschritten sinkt</li>
        <li>
          Abstimmungsschleifen werden weniger (Anzahl Rückfragen/Loops pro Vorgang oder Ticket).
        </li>
        <li>
          Meetinglast für den Prozess sinkt (weniger Prozess-Meetings oder kürzere
          Entscheidungsmeetings, weil Entscheidungspunkte klar sind).
        </li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Risiko/Nebenwirkung (und Abfederung)">
      <p>
        Prozessarbeit kann wie Kontrolle wirken („Jetzt werden wir vermessen“). Wir federn das ab,
        indem wir den Fokus auf Entlastung und Wirksamkeit legen, nicht auf Schuld – und gemeinsam
        definieren, welche Messsignale sinnvoll sind.
      </p>
    </BeratungSection>

    <BeratungSection heading="Kostenlose Erstberatung">
      <p>
        Wir klären in einem ersten Gespräch, welche Stellhebel (Struktur, Entscheidungspunkte,
        Kommunikationsarchitektur) in Ihrem Kontext den größten Unterschied machen können.
      </p>
      <Link
        to="/kontakt"
        className="mt-2 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
      >
        Erstgespräch vereinbaren
        <ArrowRight className="h-4 w-4" />
      </Link>
    </BeratungSection>
  </BeratungDetailLayout>
);

export default BeratungProzessberatung;
